import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { billingPath, channelsPath } from "@/utils/accountRoutes";

/**
 * Home v2 — account-state derivation.
 *
 * This is the client-side half of the `GET /api/internal/home/state` contract:
 * it turns real account signals into the single `scenario` the page renders
 * against. Keeping the derivation here (rather than branching inside each
 * component) is what lets the layout stay fixed while the content adapts — the
 * whole point of the redesign.
 *
 * Precedence is severity-ordered and deliberate: a suspended account that is
 * also mid-onboarding must surface the billing problem, never the checklist.
 * Onboarding outranks trial because it is the more actionable of the two — a
 * brand-new account is always both.
 */

const TRIAL_WARNING_DAYS = 7;

export function useHomeState({ onboarding, channelHealth, accountId } = {}) {
  const {
    user,
    billingStatus,
    billingPaymentLink,
    planName,
    isTrial,
    trialDaysRemaining,
    hasMinLevel,
    hasPermission,
  } = useAuth();

  // The URL param wins over the token: on a switched superadmin session both
  // agree, but the page is the one that resolved which account it is showing.
  const targetAccountId = accountId || user?.accountId;

  const scenario = useMemo(() => {
    if (billingStatus === "overdue" || billingStatus === "suspended") {
      return "debito";
    }
    if (channelHealth?.disconnectedCount > 0) return "canal";
    if (onboarding && onboarding.isComplete === false) return "onboarding";
    if (isTrial && trialDaysRemaining > 0 && trialDaysRemaining <= TRIAL_WARNING_DAYS) {
      return "trial";
    }
    return "ok";
  }, [billingStatus, channelHealth, onboarding, isTrial, trialDaysRemaining]);

  /**
   * The one high-priority signal that earns a place above the fold. Only the
   * three interrupting states produce one — `ok` and `onboarding` render the
   * greeting unobstructed, since the onboarding checklist already carries its
   * own call to action further down.
   */
  const alert = useMemo(() => {
    if (scenario === "debito") {
      return {
        color: "#ff5a4d",
        text: "Fatura vencida",
        detail: "Acesso suspenso",
        cta: "Pagar agora",
        href: billingPaymentLink || billingPath(targetAccountId),
      };
    }
    if (scenario === "canal") {
      const queued = channelHealth?.queuedMessages;
      return {
        color: "#ffa828",
        text: channelHealth?.channelName
          ? `${channelHealth.channelName} desconectado`
          : "Canal desconectado",
        detail: queued ? `${queued} mensagens paradas na fila` : "mensagens não estão saindo",
        cta: "Reconectar",
        href: channelsPath(targetAccountId),
      };
    }
    if (scenario === "trial") {
      return {
        color: "#8b8bff",
        text:
          trialDaysRemaining === 1
            ? "Seu teste termina amanhã"
            : `Seu teste termina em ${trialDaysRemaining} dias`,
        detail: "depois disso o envio é pausado",
        cta: "Escolher plano",
        href: billingPath(targetAccountId),
      };
    }
    return null;
  }, [scenario, billingPaymentLink, channelHealth, trialDaysRemaining, targetAccountId]);

  /**
   * Lucas is withdrawn while the account is blocked for billing.
   *
   * `billingGate()` already guards `/api/internal/support`: an `overdue` or
   * `suspended` account gets 402 BILLING_BLOCKED. The screen was still offering
   * the field, the shortcuts and the send button — inviting someone to type a
   * question that can only fail. The condition mirrors the gate exactly (and
   * `BlockedAccountOverlay`, which uses the same pair); restricting only on
   * `suspended` would leave an overdue account with an open field and an error
   * on submit.
   */
  const lucasRestriction = useMemo(() => {
    const blocked =
      billingStatus === "overdue" || billingStatus === "suspended";
    if (!blocked) return null;
    return {
      title: "Lucas está indisponível",
      detail: "Regularize a fatura para voltar a usar o atendimento.",
      cta: "Pagar agora",
      href: billingPaymentLink || billingPath(targetAccountId),
    };
  }, [billingStatus, billingPaymentLink, targetAccountId]);

  /**
   * Billing is visible in the footer strip only to whoever can act on it —
   * matching the gate the legacy dashboard already applied.
   */
  const canSeeBilling =
    (typeof hasMinLevel === "function" && hasMinLevel(50)) ||
    (typeof hasPermission === "function" && hasPermission("billing.read"));

  const billing = useMemo(() => {
    if (!canSeeBilling) return null;
    if (billingStatus === "overdue") {
      return { label: "Fatura vencida", status: "VENCIDA", tone: "danger" };
    }
    if (billingStatus === "suspended") {
      return { label: "Conta suspensa", status: "SUSPENSA", tone: "danger" };
    }
    if (isTrial) {
      return {
        label:
          trialDaysRemaining === 1
            ? "Teste termina amanhã"
            : `Teste termina em ${trialDaysRemaining} dias`,
        status: "TRIAL",
        tone: "info",
      };
    }
    return { label: planName || "Plano ativo", status: "EM DIA", tone: "success" };
  }, [canSeeBilling, billingStatus, isTrial, trialDaysRemaining, planName]);

  const firstName = (user?.fullName || "").trim().split(/\s+/)[0] || null;

  return {
    scenario,
    alert,
    billing,
    lucasRestriction,
    greeting: firstName ? `Oi, ${firstName}. Como posso te ajudar hoje?` : "Como posso te ajudar hoje?",
  };
}

export default useHomeState;
